export default angular.module('eventix.common.services.relationAPI', [])
    /**
     * @ngdoc service
     * @name eventix.common.services.RelationAPI
     * @description
     * Manages semi-arbitrary relations between models that are not required by the data model. Such relations
     * can be useful for presenting data to a user. (i.e. shops for a specific event)
     */
    .factory('RelationAPI', RelationAPI).name;

function RelationAPI($http, $q, $state, Company, $rootScope) {
    var cache = new RelationCache();
    $rootScope.$on('clearCache', () => RelationAPI.clearCache());

    var RelationAPI = {
        /**
         * @ngdoc method
         * @name eventix.common.services.RelationAPI#query
         * @methodOf eventix.common.services.RelationAPI
         * @description
         * Get all rows for a type of relationship
         *
         * @param {String} name Name of the relationship
         * @return {Promise<Array>} Resolves with an array of relations `[{name, left, right}]`
         */
        query(name) {
            let url = `/relations/${getCompanyGuid()}/${name}`;
            if(cache.isCached(url))
                return $q.resolve(cache.query(name));
            return $http.get(url)
                .then(res => {
                    cache.cache(url, name, res.data);
                    return res.data || [];
                });
        },
        /**
         * @ngdoc method
         * @name eventix.common.services.RelationAPI#get
         * @methodOf eventix.common.services.RelationAPI
         * @description
         * Get all relations for a type and model
         *
         * @param {String} name Name of the relationship
         * @param {String} left Left member of relationship
         * @param {String} [type=null] Can be `inverse (left = right)` or `symmetric (left || right)`
         * @return {Promise<Array>} Resolves with an array of relations `[{name, left, right}]`
         */
        get(name, left, type) {
            let url = `/relations/${getCompanyGuid()}/${name}/${left}/${type || ''}`;
            if(cache.isCached(url))
                return $q.resolve(cache.query(name, left, type));
            return $http.get(url)
                .then(res => {
                    cache.cache(url, name, res.data);
                    return res.data || [];
                })
                .catch(res => {
                    if(res.statusCode === 404)
                        return [];
                    return $q.reject(res);
                });
        },
        /**
         * @ngdoc method
         * @name eventix.common.services.RelationAPI#create
         * @methodOf eventix.common.services.RelationAPI
         * @description
         * Create a relationship between to models
         *
         * @param {String} name Name of the relationship
         * @param {String} left Left member of relationship
         * @param {String} right Right member of relationship
         * @param {String} [type=null] Can be `inverse (left = right)` or `symmetric (left || right)`
         * @return {Promise<void>} Resolves with void
         */
        create(name, left, right, type) {
            return $http.post(`/relations/${getCompanyGuid()}/${name}/${type || ''}`, {
                left: left,
                right: right
            }).then(() => {
                cache.add(name, left, right, type);
            });
        },
        /**
         * @ngdoc method
         * @name eventix.common.services.RelationAPI#delete
         * @methodOf eventix.common.services.RelationAPI
         * @description
         * Delete one or more relationships
         *
         * @param {String} name Name of the relationship
         * @param {String} left Left member of relationship
         * @param {String} [right=null] Right member of relationship
         * @param {String} [type=null] Can be `inverse (left = right)` or `symmetric (left || right)`
         * @return {Promise<void>} Resolves with void
         */
        delete: function(name, left, right, type) {
            let lr = { left: left };
            if(right) lr.right = right;
            return $http.delete(`/relations/${getCompanyGuid()}/${name}/${type || ''}`, lr)
                .then(() => cache.delete(name, left, right, type));
        },
        /**
         * @ngdoc method
         * @name eventix.common.services.RelationAPI#exists
         * @methodOf eventix.common.services.RelationAPI
         * @description
         * Check if one or more relationships of this type exist
         *
         * @param {String} name Name of the relationship
         * @param {String} left Left member of relationship
         * @param {String} right Right member of relationship
         * @param {String} [type=null] Can be `inverse (left = right)` or `symmetric (left || right)`
         * @return {Promise<Boolean>} Resolves with boolean
         */
        exists(name, left, right, type) {
            return $http.get(`/relations/${getCompanyGuid()}/${name}/${left}/${right}/${type || ''}`)
                .then(res => res.data && res.data.length > 0);
        },
        /**
         * @ngdoc method
         * @name eventix.common.services.RelationAPI#clearCache
         * @methodOf eventix.common.services.RelationAPI
         * @description
         * Clear the relationship cache
         */
        clearCache() {
            cache = new RelationCache();
        }
    };

    function getCompanyGuid() {
        return _.get($state.params.as ? Company.cached[$state.params.as] : Company.cached.me, 'guid');
    }

    function RelationCache() {}

    RelationCache.prototype.addQuery = function(url) {
        this.cachedQueries = this.cachedQueries || [];
        this.cachedQueries.push(url);
    };

    RelationCache.prototype.isCached = function(url) {
        let q = _.find(this.cachedQueries, q => q === url);
        return !_.isNil(q);
    };

    RelationCache.prototype.cache = function(url, name, data) {
        this.addQuery(url);
        _.forEach(data, d => this.add(name, d.left, d.right));
    };

    RelationCache.prototype.add = function(name, left, right, type) {
        this[name] = this[name] || [];
        switch(type) {
            case 'inverse':
                if(_.findIndex(this[name], { left: right, right: left }) < 0)
                    this[name].push({ left: right, right: left });
                break;
            case 'symmetric':
                if(_.findIndex(this[name], { left: left, right: right }) < 0)
                    this[name].push({ left: left, right: right });
                if(_.findIndex(this[name], { left: right, right: left }) < 0)
                    this[name].push({ left: right, right: left });
                break;
            default:
                if(_.findIndex(this[name], { left: left, right: right }) < 0)
                    this[name].push({ left: left, right: right });
                break;
        }
    };

    RelationCache.prototype.query = function(name, left, type) {
        if(!left)
            return _.cloneDeep(this[name]);
        var filter;
        switch(type) {
            case 'inverse':
                filter = { right: left };
                return _.cloneDeep(_.filter(this[name], filter));
            case 'symmetric':
                filter = { left: left };
                let results = _.cloneDeep(_.filter(this[name], filter));
                filter = { right: left };
                return _.cloneDeep(results.concat(_.filter(this[name], filter)));
            default:
                filter = { left: left };
                return _.cloneDeep(_.filter(this[name], filter));
        }
    };

    RelationCache.prototype.delete = function(name, left, right, type) {
        var filter;
        switch(type) {
            case 'inverse':
                filter = { right: left };
                if(right)
                    filter.left = right;
                return _.remove(this[name], filter);
            case 'symmetric':
                filter = { left: left };
                if(right)
                    filter.right = right;
                let results = _.remove(this[name], filter);
                filter = { right: left };
                if(right)
                    filter.left = right;
                return results.concat(_.remove(this[name], filter));
            default:
                return _.remove(this[name], { left: left, right: right });
        }
    };

    return RelationAPI;
}
